import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Avatar,
  ButtonDS,
  NotInterestedOutlined,
  SidePanelDS,
  Spacer,
  StyledFlex,
  Tag,
} from "@qivia/ui";
import styled from "styled-components";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import { useTranslation } from "react-i18next";
import {
  cancelPasswordCreationRequestAsync,
  relaunchPasswordCreationRequestAsync,
  selectManagersList,
} from "../usersSlice";
import { ManagerDataDisplayRow } from "../usersAPI";
import { statusColor } from "../lib";
import { selectManager } from "../../homeSlice";
import { DeactivateUserModal } from "../modals/deactivateUserModal";
import { RemoveCompanyFromCompaniesAccessModal } from "../notUse/removeCompanyFromCompaniesAccessModal";
import "../../../../i18n/i18n";
import { SidePanelUserDetails } from "./sidePanelUserDetails";
import { SidePanelUserEntities } from "./sidePanelUserEntities";
import { SidePanelUserDepartments } from "./sidePanelUserDepartments";

export const UserManagementSidePanel: FunctionComponent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const dispatch = useAppDispatch();
  const managersList = useAppSelector(selectManagersList);
  const currentManager = useAppSelector(selectManager);

  const manager: ManagerDataDisplayRow | null = useMemo(
    () => managersList.find((m) => m.uuid === params.id) ?? null,
    [managersList, params.id],
  );
  const [isDeactivateModalVisible, setIsDeactivateModalVisible] =
    useState(false);
  const [
    isRemoveCompanyFromCompaniesAccessModalVisible,
    setIsRemoveCompanyFromCompaniesAccessModalVisible,
  ] = useState(false);

  useEffect(() => {
    if (!manager) {
      navigate(`/home/users/management`);
    }
  }, [navigate, manager]);

  const isOwnerManager: boolean = useMemo(() => {
    return currentManager?.role === "OWNER";
  }, [currentManager]);

  const canDeactivateManager: boolean = useMemo(() => {
    return (
      currentManager?.role === "OWNER" &&
      manager?.status === "ACTIVE" &&
      manager.role !== "OWNER" &&
      params.id !== currentManager.uuid
    );
  }, [currentManager, manager, params.id]);

  const canUpdateRoleManager: boolean = useMemo(() => {
    return (
      currentManager?.role === "OWNER" &&
      manager?.role !== "OWNER" &&
      params.id !== currentManager.uuid
    );
  }, [currentManager, manager, params.id]);

  const isLastCreatedPasswordRequestCanceled = useMemo(
    () => manager?.status && manager?.status === "CANCELED",
    [manager?.status],
  );

  const isLastCreatedPasswordRequestExpired = useMemo(
    () => manager?.status && manager?.status === "EXPIRED",
    [manager?.status],
  );

  const canCancelPasswordRequest = useMemo(
    () =>
      !isLastCreatedPasswordRequestCanceled &&
      !isLastCreatedPasswordRequestExpired,
    [isLastCreatedPasswordRequestCanceled, isLastCreatedPasswordRequestExpired],
  );

  const isPendingUser = useMemo(
    () =>
      manager?.status &&
      manager?.status !== "ACTIVE" &&
      manager?.status !== "DEACTIVATED",
    [manager?.status],
  );

  const onSidePanelClosed = useCallback(() => {
    navigate("/home/users/management");
  }, [navigate]);

  if (!manager || !currentManager) {
    return;
  }

  return (
    <SidePanelDS.SidePanel
      visible={!!manager}
      onClose={onSidePanelClosed}
      absoluteSection={
        canDeactivateManager ? (
          <ButtonDS
            leftIcon={<NotInterestedOutlined />}
            text={t("users.management.sidePanel.deactivate")}
            format={"fill"}
            buttonType={"secondary"}
            onClick={() =>
              setIsDeactivateModalVisible(!isDeactivateModalVisible)
            }
            iconColor={"errorNormal"}
          />
        ) : undefined
      }
    >
      <SidePanelDS.Section
        verticalSpacing={2}
        horizontalSpacing={1.5}
        background={colors["colors/button/secondary/hover"]}
      >
        <DeactivateUserModal
          isVisible={isDeactivateModalVisible}
          onCloseModal={() => {
            setIsDeactivateModalVisible(false);
          }}
          manager={manager}
        />
        <RemoveCompanyFromCompaniesAccessModal
          isVisible={isRemoveCompanyFromCompaniesAccessModalVisible}
          onCloseModal={() => {
            setIsRemoveCompanyFromCompaniesAccessModalVisible(false);
          }}
          manager={manager}
        />
        <StyledPanelContainer>
          <Tag
            text={t(`users.management.status.${manager.status}`)}
            backgroundColor={statusColor[manager.status].backgroundColor}
            textColor={statusColor[manager.status].textColor}
          />
          <Spacer y={1.5} />
          <Avatar
            size={"XL"}
            color={"mint"}
            firstName={manager["firstName"]}
            lastName={manager["lastName"]}
          />
          <Spacer y={1.5} />
          <StyledName>{manager.name}</StyledName>
          <Spacer y={0.5} />
          <StyledEmail>{manager.email}</StyledEmail>

          {isOwnerManager && isPendingUser && (
            <>
              <Spacer y={2.5} />
              <StyledFlex>
                <ButtonDS
                  text={t("users.management.sidePanel.relaunch")}
                  format={"hug"}
                  buttonType={"primary"}
                  onClick={() =>
                    manager.uuid &&
                    void dispatch(
                      relaunchPasswordCreationRequestAsync({
                        managerUuid: manager.uuid,
                      }),
                    )
                  }
                />
                {canCancelPasswordRequest && (
                  <>
                    <Spacer x={0.5} />
                    <ButtonDS
                      text={t("users.management.sidePanel.cancel")}
                      format={"hug"}
                      buttonType={"secondary"}
                      onClick={() =>
                        manager.uuid &&
                        void dispatch(
                          cancelPasswordCreationRequestAsync({
                            managerUuid: manager.uuid,
                          }),
                        )
                      }
                    />
                  </>
                )}
              </StyledFlex>
            </>
          )}
        </StyledPanelContainer>
      </SidePanelDS.Section>
      <SidePanelDS.Section horizontalSpacing={1.5} verticalSpacing={2}>
        <SidePanelUserDetails
          manager={manager}
          canUpdateRole={canUpdateRoleManager}
        />
      </SidePanelDS.Section>
      <SidePanelUserEntities
        managerRole={manager.role}
        companiesAccess={manager.companiesAccess}
      />
      <SidePanelUserDepartments
        managerRole={manager.role}
        departmentsAccess={manager.departmentsAccess}
      />
    </SidePanelDS.SidePanel>
  );
};

const StyledPanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const StyledName = styled.div`
  ${typographies["Header/H1"]};
  color: ${colors["colors/text/black"]};
`;

const StyledEmail = styled.div`
  ${typographies["Body/M"]};
  color: ${colors["colors/text/darkGrey"]};
  text-align: center;
`;
